/**
 * @fileoverview Funciones de validación de coordenadas UTM para Andalucía
 * @module lib/coordinate/validators
 * 
 * Extraído de coordinateNormalizer.ts v2.12 como parte de TD-002.D
 * para mejorar mantenibilidad y organización del código.
 * 
 * @version 1.0.0
 * @date 2026-01-27
 */

import { RANGOS_ANDALUCIA } from './constants';
import type { ResultadoValidacion } from './types';

// ============================================================================
// VALIDACIÓN DE COORDENADAS INDIVIDUALES
// ============================================================================

/**
 * Valida si un valor numérico corresponde a una coordenada UTM válida en Andalucía.
 * 
 * Detecta automáticamente:
 * - Coordenadas X UTM (rango 100,000 - 620,000)
 * - Coordenadas Y UTM (rango 3,980,000 - 4,290,000)
 * - Coordenadas Y truncadas (sin el prefijo "4", rango 40,000 - 300,000)
 * - Coordenadas geográficas (latitud/longitud en grados decimales)
 * 
 * @param valor - Valor numérico a validar
 * @returns Resultado de validación con tipo, confianza y posibles correcciones
 * 
 * @example
 * ```typescript
 * const resultado = validarCoordenada(453821);
 * // { valido: true, tipo: 'X', confianza: 'ALTA', warnings: [] }
 * 
 * const yTruncada = validarCoordenada(85421);
 * // { valido: true, tipo: 'Y', confianza: 'MEDIA', 
 * //   warnings: ['ERROR P0-1: Y truncada detectada...'],
 * //   correccionAplicada: '+4000000', valorCorregido: 4085421 }
 * ```
 */
export function validarCoordenada(valor: number): ResultadoValidacion {
  const warnings: string[] = [];
  
  // Verificar rango X UTM
  if (valor >= RANGOS_ANDALUCIA.UTM.X_MIN && valor <= RANGOS_ANDALUCIA.UTM.X_MAX) {
    return { valido: true, tipo: 'X', confianza: 'ALTA', warnings };
  }
  
  // Verificar rango Y UTM
  if (valor >= RANGOS_ANDALUCIA.UTM.Y_MIN && valor <= RANGOS_ANDALUCIA.UTM.Y_MAX) {
    return { valido: true, tipo: 'Y', confianza: 'ALTA', warnings };
  }
  
  // Verificar Y truncada (sin el "4" inicial)
  if (valor >= RANGOS_ANDALUCIA.Y_TRUNCADA.MIN && valor <= RANGOS_ANDALUCIA.Y_TRUNCADA.MAX) {
    const valorCorregido = valor + 4000000;
    if (valorCorregido >= RANGOS_ANDALUCIA.UTM.Y_MIN && 
        valorCorregido <= RANGOS_ANDALUCIA.UTM.Y_MAX) {
      warnings.push(`ERROR P0-1: Y truncada detectada. Valor ${valor} → ${valorCorregido}`);
      return {
        valido: true,
        tipo: 'Y',
        confianza: 'MEDIA',
        warnings,
        correccionAplicada: '+4000000',
        valorCorregido,
      };
    }
  }
  
  // Verificar coordenada geográfica (latitud)
  if (valor >= RANGOS_ANDALUCIA.GEOGRAFICAS.LAT_MIN && 
      valor <= RANGOS_ANDALUCIA.GEOGRAFICAS.LAT_MAX) {
    warnings.push('Coordenada geográfica detectada (latitud). Requiere conversión a UTM.');
    return { valido: true, tipo: 'GEOGRAFICA_LAT', confianza: 'ALTA', warnings };
  }
  
  // Verificar coordenada geográfica (longitud)
  if (valor >= RANGOS_ANDALUCIA.GEOGRAFICAS.LON_MIN && 
      valor <= RANGOS_ANDALUCIA.GEOGRAFICAS.LON_MAX) {
    warnings.push('Coordenada geográfica detectada (longitud). Requiere conversión a UTM.');
    return { valido: true, tipo: 'GEOGRAFICA_LON', confianza: 'ALTA', warnings };
  }
  
  // Valor fuera de todos los rangos válidos
  warnings.push(`Valor ${valor} fuera de rangos válidos para Andalucía`);
  return { valido: false, tipo: 'DESCONOCIDO', confianza: 'BAJA', warnings };
}

// ============================================================================
// DETECCIÓN DE INTERCAMBIO X↔Y
// ============================================================================

/**
 * Resultado de la detección de intercambio X↔Y
 */
export interface ResultadoIntercambioXY {
  /** true si se detectó intercambio */
  intercambiado: boolean;
  /** Valor X corregido (o el original si no hubo intercambio) */
  xCorregida: number;
  /** Valor Y corregido (o el original si no hubo intercambio) */
  yCorregida: number;
  /** Mensaje descriptivo si hubo intercambio */
  mensaje?: string;
}

/**
 * Detecta si las coordenadas X e Y están intercambiadas.
 * 
 * Heurística:
 * - X parece Y: valor entre 2,000,000 y 5,000,000
 * - Y parece X: valor entre 100,000 y 700,000
 * 
 * @param x - Valor de la coordenada X
 * @param y - Valor de la coordenada Y
 * @returns Resultado con indicador de intercambio y valores corregidos
 * 
 * @example
 * ```typescript
 * const resultado = detectarIntercambioXY(4085421, 453821);
 * // { intercambiado: true, xCorregida: 453821, yCorregida: 4085421,
 * //   mensaje: 'ERROR P0-2: Intercambio X↔Y detectado...' }
 * 
 * const normal = detectarIntercambioXY(453821, 4085421);
 * // { intercambiado: false, xCorregida: 453821, yCorregida: 4085421 }
 * ```
 */
export function detectarIntercambioXY(x: number, y: number): ResultadoIntercambioXY {
  const xPareceSiendoY = x >= 2000000 && x <= 5000000;
  const yPareceSiendoX = y >= 100000 && y <= 700000;
  
  if (xPareceSiendoY && yPareceSiendoX) {
    return {
      intercambiado: true,
      xCorregida: y,
      yCorregida: x,
      mensaje: `ERROR P0-2: Intercambio X↔Y detectado. (${x}, ${y}) → (${y}, ${x})`,
    };
  }
  
  return { intercambiado: false, xCorregida: x, yCorregida: y };
}

// ============================================================================
// M05 — RESCATE SWAP X↔Y (ÚLTIMA DEFENSA)
// ============================================================================

/**
 * Resultado extendido del rescate swap con info de formato detectado.
 */
export interface ResultadoRescateSwap extends ResultadoIntercambioXY {
  /** Formato detectado tras el swap: 'UTM' o 'DD' (grados decimales) */
  formatoDetectado?: 'UTM' | 'DD';
  /** Estrategia aplicada */
  estrategia?: 'swap_utm' | 'swap_dd' | 'swap_dd_abs' | 'swap_utm_abs';
}

/**
 * Intenta rescatar un par de coordenadas intercambiando X↔Y como última defensa.
 *
 * Se invoca cuando el par original no es válido tras las correcciones estándar
 * (truncación, dígito extra, swap básico). Prueba 4 estrategias:
 *
 * 1. **swap_utm** — Intercambio directo: ¿(Y, X) cae en rangos UTM Andalucía?
 * 2. **swap_dd** — Intercambio DD: ¿X parece latitud y Y parece longitud?
 * 3. **swap_dd_abs** — DD con signo perdido: ¿X es lat y |Y| es lon? (lon España < 0)
 * 4. **swap_utm_abs** — UTM con signo espurio: ¿|Y| es X_UTM y X es Y_UTM?
 *
 * @param x - Coordenada X actual (posiblemente incorrecta)
 * @param y - Coordenada Y actual (posiblemente incorrecta)
 * @returns Resultado con indicador de intercambio y estrategia aplicada
 *
 * @example
 * ```typescript
 * // DD swap: lat en X, lon en Y
 * intentarRescateSwap(37.18, -3.60)
 * // → { intercambiado: true, xCorregida: -3.60, yCorregida: 37.18,
 * //     formatoDetectado: 'DD', estrategia: 'swap_dd' }
 *
 * // UTM con signo espurio
 * intentarRescateSwap(4085421, -453821)
 * // → { intercambiado: true, xCorregida: 453821, yCorregida: 4085421,
 * //     formatoDetectado: 'UTM', estrategia: 'swap_utm_abs' }
 * ```
 */
export function intentarRescateSwap(x: number, y: number): ResultadoRescateSwap {
  const { UTM, GEOGRAFICAS } = RANGOS_ANDALUCIA;
  const noSwap: ResultadoRescateSwap = { intercambiado: false, xCorregida: x, yCorregida: y };

  // Ya válido en UTM? No rescatar.
  if (x >= UTM.X_MIN && x <= UTM.X_MAX && y >= UTM.Y_MIN && y <= UTM.Y_MAX) {
    return noSwap;
  }

  // Estrategia 1: swap_utm — Intercambio directo
  if (y >= UTM.X_MIN && y <= UTM.X_MAX && x >= UTM.Y_MIN && x <= UTM.Y_MAX) {
    return {
      intercambiado: true, xCorregida: y, yCorregida: x,
      formatoDetectado: 'UTM', estrategia: 'swap_utm',
      mensaje: `M05 Rescate swap UTM: (${x}, ${y}) → (${y}, ${x})`,
    };
  }

  // Estrategia 2: swap_dd — X parece latitud, Y parece longitud (negativa)
  const xIsLat = x >= GEOGRAFICAS.LAT_MIN && x <= GEOGRAFICAS.LAT_MAX;
  const yIsLon = y >= GEOGRAFICAS.LON_MIN && y <= GEOGRAFICAS.LON_MAX;
  if (xIsLat && yIsLon) {
    return {
      intercambiado: true, xCorregida: y, yCorregida: x,
      formatoDetectado: 'DD', estrategia: 'swap_dd',
      mensaje: `M05 Rescate swap DD: lat=${x}° lon=${y}° → X=${y}, Y=${x}`,
    };
  }

  // Estrategia 3: swap_dd_abs — X es lat, |Y| es lon (signo perdido)
  const absY = Math.abs(y);
  const absYIsLon = absY >= Math.abs(GEOGRAFICAS.LON_MAX) && absY <= Math.abs(GEOGRAFICAS.LON_MIN);
  if (xIsLat && absYIsLon && y > 0) {
    return {
      intercambiado: true, xCorregida: -absY, yCorregida: x,
      formatoDetectado: 'DD', estrategia: 'swap_dd_abs',
      mensaje: `M05 Rescate swap DD (signo): lat=${x}° lon=-${absY}° → X=-${absY}, Y=${x}`,
    };
  }

  // Estrategia 4: swap_utm_abs — UTM con signo espurio en una coordenada
  const absX = Math.abs(x);
  if (absX >= UTM.X_MIN && absX <= UTM.X_MAX && y >= UTM.Y_MIN && y <= UTM.Y_MAX && x < 0) {
    return {
      intercambiado: false, xCorregida: absX, yCorregida: y,
      formatoDetectado: 'UTM', estrategia: 'swap_utm_abs',
      mensaje: `M05 Corrección signo X: (${x}, ${y}) → (${absX}, ${y})`,
    };
  }
  if (y < 0 && absY >= UTM.X_MIN && absY <= UTM.X_MAX && x >= UTM.Y_MIN && x <= UTM.Y_MAX) {
    return {
      intercambiado: true, xCorregida: absY, yCorregida: x,
      formatoDetectado: 'UTM', estrategia: 'swap_utm_abs',
      mensaje: `M05 Rescate swap UTM + signo: (${x}, ${y}) → (${absY}, ${x})`,
    };
  }

  return noSwap;
}
